// src/components/ProcessManager/Childs/ProcessManagerCommercialTrackingTable.tsx
import React, { useMemo } from 'react';
import { IProcessManager } from '../../Interfaces/IProcessManager';
import { ICourt } from '../../Interfaces/ICourt';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  Box,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Link } from '@mui/icons-material';
import { useGetLegalClients } from '../../hooks/reactQuerys/LegalClientQueries';

interface IProps {
  title: string; // "Handelsregister" oder "Gesellschafterliste"
  courts: ICourt[];
  processManagerArray: IProcessManager[]; // nur Typ 20/21 reinreichen
}

type GroupKey = number | 'unknown';

export const ProcessManagerCommercialTrackingTable: React.FC<IProps> = ({
  title,
  courts,
  processManagerArray,
}) => {
  const navigate = useNavigate();
  const { legalClients, isLoadingLegalClients } = useGetLegalClients();

  // Map<idLegalClient, LegalClient>
  const lcById = useMemo(() => {
    const m = new Map<number, any>();
    (legalClients?.results ?? []).forEach((lc: any) =>
      m.set(lc.idLegalClient, lc)
    );
    return m;
  }, [legalClients]);

  // Gruppiere PMs nach Gericht; PMs ohne Gericht in 'unknown'
  const groups = useMemo(() => {
    const g = new Map<GroupKey, IProcessManager[]>();
    processManagerArray.forEach(pm => {
      const idLc = (pm.data as any)?.idLegalClient ?? pm.idLegalClient ?? null;
      const lc = idLc ? lcById.get(idLc) : undefined;
      const idCourt = (lc?.idCourt ?? lc?.LegalClient?.idCourt ?? null) as
        | number
        | null;
      const key: GroupKey = typeof idCourt === 'number' ? idCourt : 'unknown';
      const arr = g.get(key) ?? [];
      arr.push(pm);
      g.set(key, arr);
    });
    return g;
  }, [processManagerArray, lcById]);

  const knownCourtIds = useMemo(
    () => Array.from(groups.keys()).filter((k): k is number => k !== 'unknown'),
    [groups]
  );

  const getStateDot = (pm: IProcessManager) => {
    const today = new Date();
    const dt = pm.DateToCheck ? new Date(String(pm.DateToCheck)) : today;
    const diffDays = Math.floor((today.getTime() - dt.getTime()) / 86400000);
    let color = '#4caf50';
    if (pm.idProcessManagerState !== 1) color = '#eeeeee';
    else if (diffDays > 14) color = '#f44336';
    else if (diffDays > 7) color = '#ffeb3b';
    return (
      <Tooltip
        title={
          pm.idProcessManagerState !== 1
            ? `Nachverfolgung am ${new Date(String(pm.Closed_at)).toLocaleString()} abgeschlossen`
            : `Prüfung seit ${diffDays} Tagen fällig`
        }
      >
        <Box
          sx={{
            width: 16,
            height: 16,
            borderRadius: '50%',
            backgroundColor: color,
            border: '1px solid rgba(0,0,0,0.1)',
            display: 'inline-block',
          }}
        />
      </Tooltip>
    );
  };

  if (!processManagerArray.length) return null;
  if (isLoadingLegalClients) return <CircularProgress />;
  if (!legalClients)
    return (
      <Alert severity='error'>Firmendaten konnten nicht geladen werden.</Alert>
    );

  return (
    <>
      <Typography variant='h5' sx={{ mt: 3 }}>
        {title}
      </Typography>

      {/* bekannte Gerichte */}
      {knownCourtIds
        .map(id => courts.find(c => c.idCourt === id))
        .filter((c): c is ICourt => !!c)
        .sort((a, b) => a.Court.localeCompare(b.Court))
        .map(court => (
          <Accordion
            key={`pm-commercial-court-${court.idCourt}`}
            defaultExpanded
          >
            <AccordionSummary>{court.Court}</AccordionSummary>
            <AccordionDetails>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Erstellt am</TableCell>
                    <TableCell>Art</TableCell>
                    <TableCell>Prüfungen</TableCell>
                    <TableCell>Letzte Prüfung</TableCell>
                    <TableCell>Prüfungsdatum</TableCell>
                    <TableCell>Registergericht</TableCell>
                    <TableCell>Registernummer</TableCell>
                    <TableCell>Firma</TableCell>
                    <TableCell>Aktion</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groups.get(court.idCourt)!.map(pm => {
                    const idLc =
                      (pm.data as any)?.idLegalClient ??
                      pm.idLegalClient ??
                      null;
                    const lc = idLc ? lcById.get(idLc) : undefined;

                    const registerCourt =
                      lc?.Court ??
                      lc?.LegalClient?.Court ??
                      'Fehlende Informationen';
                    const registerNumber =
                      lc?.RegisterNumber ??
                      lc?.LegalClient?.RegisterNumber ??
                      'Fehlende Informationen';
                    const company =
                      lc?.LastName?.trim() ||
                      lc?.SurName?.trim() ||
                      [lc?.FirstName, lc?.LastName]
                        .filter(Boolean)
                        .join(' ')
                        .trim() ||
                      lc?.LegalClient?.LastName ||
                      'Fehlende Informationen';

                    return (
                      <TableRow key={`pm-${pm.idProcessManager}`}>
                        <TableCell>{getStateDot(pm)}</TableCell>
                        <TableCell>
                          {new Date(pm.Created_at).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          {pm.idProcessManagerType === 20
                            ? 'Handelsregister'
                            : 'Gesellschafterliste'}
                        </TableCell>
                        <TableCell>{pm.Tries}</TableCell>
                        <TableCell>
                          {pm.LastTry_at
                            ? new Date(pm.LastTry_at).toLocaleString()
                            : '-'}
                        </TableCell>
                        <TableCell>
                          {pm.DateToCheck
                            ? new Date(pm.DateToCheck).toLocaleDateString()
                            : '-'}
                        </TableCell>
                        <TableCell>{registerCourt}</TableCell>
                        <TableCell>{registerNumber}</TableCell>
                        <TableCell>{company}</TableCell>
                        <TableCell>
                          <IconButton
                            onClick={() =>
                              navigate(
                                `/legaltransaction/${pm.idLegalTransaction}`
                              )
                            }
                          >
                            <Link />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
        ))}

      {/* Unbekanntes Gericht */}
      {groups.has('unknown') && (
        <Accordion key='pm-commercial-court-unknown' defaultExpanded>
          <AccordionSummary>Unbekanntes Gericht</AccordionSummary>
          <AccordionDetails>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Erstellt am</TableCell>
                  <TableCell>Art</TableCell>
                  <TableCell>Prüfungen</TableCell>
                  <TableCell>Letzte Prüfung</TableCell>
                  <TableCell>Prüfungsdatum</TableCell>
                  <TableCell>Registergericht</TableCell>
                  <TableCell>Registernummer</TableCell>
                  <TableCell>Firma</TableCell>
                  <TableCell>Aktion</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.get('unknown')!.map(pm => {
                  const idLc =
                    (pm.data as any)?.idLegalClient ?? pm.idLegalClient ?? null;
                  const lc = idLc ? lcById.get(idLc) : undefined;

                  const registerCourt =
                    lc?.Court ??
                    lc?.LegalClient?.Court ??
                    'Fehlende Informationen';
                  const registerNumber =
                    lc?.RegisterNumber ??
                    lc?.LegalClient?.RegisterNumber ??
                    'Fehlende Informationen';
                  const company =
                    lc?.LastName?.trim() ||
                    lc?.SurName?.trim() ||
                    [lc?.FirstName, lc?.LastName]
                      .filter(Boolean)
                      .join(' ')
                      .trim() ||
                    lc?.LegalClient?.LastName ||
                    'Fehlende Informationen';

                  return (
                    <TableRow key={`pm-unknown-${pm.idProcessManager}`}>
                      <TableCell>{getStateDot(pm)}</TableCell>
                      <TableCell>
                        {new Date(pm.Created_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {pm.idProcessManagerType === 20
                          ? 'Handelsregister'
                          : 'Gesellschafterliste'}
                      </TableCell>
                      <TableCell>{pm.Tries}</TableCell>
                      <TableCell>
                        {pm.LastTry_at
                          ? new Date(pm.LastTry_at).toLocaleString()
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {pm.DateToCheck
                          ? new Date(pm.DateToCheck).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell>{registerCourt}</TableCell>
                      <TableCell>{registerNumber}</TableCell>
                      <TableCell>{company}</TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() =>
                            navigate(
                              `/legaltransaction/${pm.idLegalTransaction}`
                            )
                          }
                        >
                          <Link />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
};
